import { useEffect, useMemo, useState } from "react";
import {
  Button,
  MenuItem,
  CircularProgress,
  Grid,
  InputLabel,
  FormControl,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Snackbar,
} from "@mui/material";
import Select from "@mui/material/Select";
import { getNumber } from "src/utils/cardUtils";
import { get, put } from "src/utils/networkUtils";
import {
  defaultHockeyPackSettings,
  defaultSoccerPackSettings,
  defaultBasketballPackSettings,
} from "src/utils/packSettings";
import PositionFields from "./PositionFields";
import SPORT_POSITIONS from "src/utils/sportPositions";

export const getDefaultPackSettings = (sport) => {
  if (!sport) {
    return defaultHockeyPackSettings;
  }
  const sportLower = sport.toLowerCase();
  return sportLower === "soccer"
    ? defaultSoccerPackSettings
    : sportLower === "basketball"
    ? defaultBasketballPackSettings
    : defaultHockeyPackSettings;
};

export default function PackSettingsDialog(props) {
  const [competitions, setCompetitions] = useState(null);
  const [packSettings, setPackSettings] = useState(null);
  const [totalCard, setTotalCard] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);

  // Get sport from selected competition
  const selectedCompetition = competitions?.find(
    (c) => +c.id === +packSettings?.competitionId
  );
  const sport = selectedCompetition?.sport?.toLowerCase() || "hockey";
  const positionFields = useMemo(() => {
    const safeSport =
    SPORT_POSITIONS[sport] ? sport : "hockey";
    return ["Team", ...SPORT_POSITIONS[safeSport], "Random"];
  }, [sport]);
  
  const sumFields = (obj, fields = []) =>
    fields.reduce((total, key) => total + getNumber(+obj?.[key]), 0);

  useEffect(() => {
    get(`/admin/competitions`).then(function (response) {
      setLoading(false);
      setCompetitions(response.competitions);
    });

    get(`/admin/packs/settings?packType=${props.packType}`).then(function (
      response
    ) {
      if (response.settings.length === 0) {
        setPackSettings(getDefaultPackSettings(sport));
      } else {
        setPackSettings(response.settings);
      }
    });
  }, [props.packType, sport]);

  useEffect(() => {
    if (
      packSettings &&
      competitions &&
      competitions.length > 0 &&
      !packSettings.competitionId
    ) {
      setPackSettings((prevState) => ({
        ...prevState,
        competitionId: competitions[0].id,
      }));
    }
  }, [competitions, packSettings]);

  useEffect(() => {
    if (!packSettings) return;
    const total = sumFields(packSettings, positionFields);
    setTotalCard(total);
  }, [packSettings, positionFields]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setPackSettings((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };
  const handleGradePullChange = (value) => {
    setPackSettings((prevState) => ({
      ...prevState,
      gradePull: value,
    }));
  };
  const handleCompetitionIdChange = (value) => {
    setPackSettings((prevState) => ({
      ...prevState,
      competitionId: value,
    }));
  };

  const onSuccess = () => {
    try {
      const gradePull = JSON.parse(packSettings.gradePull);
      if (!Array.isArray(gradePull)) {
        throw new Error("gradePull is not an array");
      }
      const numberOfItems = gradePull.length;
      console.log("Number of items in gradePull:", numberOfItems);
    } catch (error) {
      console.error("Invalid gradePull JSON:", error);
      return;
    }
    const positionTotal = sumFields(packSettings, positionFields);
    const ratingTotal =
      getNumber(+packSettings?.AllStar) +
      getNumber(+packSettings?.Base) +
      getNumber(+packSettings?.Gold) +
      getNumber(+packSettings?.Silver);

    if (positionTotal === ratingTotal) {
      put(`/admin/packs/settings`, {
        settings: packSettings,
        packType: props.packType,
      }).then(function () {
        props.onSuccessHandler();
      });
    } else {
      setErrorOpen(true);
    }
  };
  const onCancel = () => {
    props.onClose();
  };
  const handleCloseError = () => {
    setErrorOpen(false);
  };
  return (
    <>
      <Dialog
        open={props.open}
        onClose={() => {}}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Edit Pack Settings</DialogTitle>
        <DialogContent>
          {packSettings === null || loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Grid container>
              <Grid item xs={15} sx={{ mt: 0, p: 1 }}>
                <Typography variant="h6">
                  Total cards across positions must equal total cards across
                  ratings. <br />
                  Grade pull percentages (if specified) take precedence over
                  number of cards for each rating.
                </Typography>
              </Grid>
              <Grid item xs={12} sx={{ mt: 0, p: 1 }}>
                Number of Cards: {totalCard}
              </Grid>
              <Grid item xs={3} sx={{ m: 0, p: 1 }}>
                <TextField
                  id="Gold"
                  label="Gold"
                  fullWidth
                  value={packSettings.Gold}
                  variant="standard"
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={3} sx={{ m: 0, p: 1 }}>
                <TextField
                  id="Silver"
                  label="Silver"
                  fullWidth
                  value={packSettings.Silver}
                  variant="standard"
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={3} sx={{ m: 0, p: 1 }}>
                <TextField
                  id="Base"
                  label="Base"
                  fullWidth
                  value={packSettings.Base}
                  variant="standard"
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={3} sx={{ m: 0, p: 1 }}>
                <TextField
                  id="AllStar"
                  label="AllStar"
                  fullWidth
                  value={packSettings.AllStar}
                  variant="standard"
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={3} sx={{ m: 0, p: 1 }}>
                <TextField
                  id="Team"
                  label="Team"
                  fullWidth
                  value={packSettings.Team}
                  variant="standard"
                  onChange={handleChange}
                />
              </Grid>
              <PositionFields
  positions={SPORT_POSITIONS[sport]}
  packSettings={packSettings}
  handleChange={handleChange}
  variant="standard"
/>
              <Grid item xs={3} sx={{ m: 0, p: 1 }}>
                <TextField
                  id="Random"
                  label="Random"
                  fullWidth
                  value={packSettings.Random}
                  variant="standard"
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={3} sx={{ m: 0, p: 1 }}>
                <TextField
                  id="gradePull"
                  label="Grade Pull %"
                  fullWidth
                  multiline
                  rows={8}
                  value={packSettings.gradePull}
                  variant="standard"
                  onChange={(e) => handleGradePullChange(e.target.value)}
                />
              </Grid>
              <Grid item xs={9} sx={{ m: 0, p: 1 }}>
                <FormControl sx={{ m: 1, minWidth: 320 }} size="small">
                  <InputLabel id="demo-simple-select-label">
                    Competition
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={packSettings.competitionId}
                    label="Competition"
                    onChange={(event) =>
                      handleCompetitionIdChange(event.target.value)
                    }
                  >
                    {competitions !== null &&
                      competitions.map((competition) => (
                        <MenuItem
                          value={competition.id}
                          key={`competition-${competition.id}`}
                        >
                          {competition.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onCancel()}>Cancel</Button>

          <Button onClick={() => onSuccess()} autoFocus disabled={false}>
            Edit
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        message="Total cards across positions must equal total cards across ratings."
      />
    </>
  );
}
